import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    Chip,
    CircularProgress,
    Fab,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
} from "@mui/material";
import {
    AddOutlined,
    ArrowBackOutlined,
    AttachFileOutlined,
    CloseOutlined,
    DownloadOutlined,
    DriveFileRenameOutlineOutlined,
    FullscreenOutlined,
    HideImageOutlined,
    SaveOutlined,
    GridOnOutlined,
    PictureAsPdfOutlined,
    PrintOutlined,
} from "@mui/icons-material";
import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    DatePicker,
    LocalizationProvider,
    MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useCookies } from "react-cookie";
import {
    CheckListSimpleResponse,
    MaintenanceFileItem,
    MaintenanceRequest,
    MaintenanceResponse,
    MaintenanceType,
    MaintenanceView,
} from "../../../../types/types";
import maintenanceTypesFindAllByActivated from "../../../../resources/Api/vehiclemaintenance/maintenanceTypesFindAllByActivated";
import maintenancesFindAllByActivated from "../../../../resources/Api/vehiclemaintenance/maintenancesFindAllByActivated";
import maintenanceSaveByVehicle from "../../../../resources/Api/vehiclemaintenance/maintenanceSaveByVehicle";
import maintenanceUpdateByVehicle from "../../../../resources/Api/vehiclemaintenance/maintenanceUpdateByVehicle";
import maintenanceExportById from "../../../../resources/Api/vehiclemaintenance/maintenanceExportById";
import driverFindAllListResponse from "../../../../resources/Api/driver/driverFindAllListResponse";
import checklistFindAllSimpleResponse from "../../../../resources/Api/checklist/checklistFindAllSimpleResponse";
import { currencyFormatter } from "../../../../functions/functions";

const exportItems = [
    {
        label: "PDF",
        icon: <PictureAsPdfOutlined />,
    },
    {
        label: "Excel",
        icon: <GridOnOutlined />,
    },
    {
        label: "Excel simples",
        icon: <GridOnOutlined />,
    },
];

type StatusMessage = "warn" | "success" | "info" | "error" | undefined;

type VehicleMaintenanceProps = {
    vehicleId: number;
    customerId: number;
    departmentId: number;
    isDisabled: boolean;
    setTextValidation: Dispatch<SetStateAction<string>>;
    setStatusMessage: Dispatch<SetStateAction<StatusMessage>>;
    setFieldsIsValid: Dispatch<SetStateAction<boolean>>;
    requestCancelConfirmation: (onConfirm: () => void) => void;
    onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

type MaintenanceDriverOption = {
    id: number;
    name: string;
    driverCode?: string;
    driverIdentifier?: string;
    driverDepartmentReducedName?: string;
};

const INITIAL_MAINTENANCE: MaintenanceRequest = {
    vehicleId: -1,
    customerId: -1,
    maintenanceTypeId: -1,
    checklistId: null,
    driverId: null,
    maintenanceDate: null,
    odometer: null,
    totalValue: null,
    description: null,
    nextMaintenanceDate: null,
    nextMaintenanceKm: null,
    observation: null,
    files: [],
    removedFiles: [],
};

const VehicleMaintenance = ({
    vehicleId,
    customerId,
    departmentId,
    isDisabled,
    setTextValidation,
    setStatusMessage,
    setFieldsIsValid,
    requestCancelConfirmation,
    onUnsavedChangesChange,
}: VehicleMaintenanceProps) => {
    const [cookies] = useCookies(["token"]);

    const [maintenancePage, setMaintenancePage] = useState<number>(0);
    const [maintenanceSize, setMaintenanceSize] = useState<number>(8);
    const [maintenanceLastPage, setMaintenanceLastPage] = useState<boolean>(true);
    const [maintenanceTotalElements, setMaintenanceTotalElements] =
        useState<number>(0);
    const [maintenanceLoading, setMaintenanceLoading] = useState<boolean>(false);
    const [maintenanceSaving, setMaintenanceSaving] = useState<boolean>(false);
    const [maintenanceTypesLoading, setMaintenanceTypesLoading] =
        useState<boolean>(false);
    const [maintenanceDriversLoading, setMaintenanceDriversLoading] =
        useState<boolean>(false);
    const [maintenanceChecklistsLoading, setMaintenanceChecklistsLoading] =
        useState<boolean>(false);
    const [maintenanceExportLoading, setMaintenanceExportLoading] =
        useState<boolean>(false);

    const [maintenanceView, setMaintenanceView] =
        useState<MaintenanceView>("list");

    const [maintenance, setMaintenance] = useState<MaintenanceRequest>({
        ...INITIAL_MAINTENANCE,
    });

    const [maintenanceList, setMaintenanceList] = useState<MaintenanceResponse[]>(
        [],
    );
    const [selectedMaintenance, setSelectedMaintenance] =
        useState<MaintenanceResponse | null>(null);
    const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>(
        [],
    );
    const [maintenanceDrivers, setMaintenanceDrivers] = useState<
        MaintenanceDriverOption[]
    >([]);
    const [maintenanceChecklists, setMaintenanceChecklists] = useState<
        CheckListSimpleResponse[]
    >([]);

    const [maintenanceFiles, setMaintenanceFiles] = useState<
        MaintenanceFileItem[]
    >([]);
    const [maintenanceRemovedFiles, setMaintenanceRemovedFiles] = useState<
        number[]
    >([]);
    const [currentMaintenanceFile, setCurrentMaintenanceFile] =
        useState<FileList | null>(null);

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));

    const [exportType, setExportType] = useState<string>("");

    const [openExport, setOpenExport] = useState<boolean>(false);

    const [maintenanceExportId, setMaintenanceExportId] = useState<number | null>(
        null,
    );

    const exportAnchorRef = useRef<any>(null);

    const maintenanceFileInputRef = useRef<HTMLInputElement>(null);

    const loadMaintenanceTypes = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        setMaintenanceTypesLoading(true);

        try {
            const response = await maintenanceTypesFindAllByActivated(
                token,
                "activated",
            );

            setMaintenanceTypes(response.data ?? []);
        } catch (error) {
            console.error("Erro ao buscar tipos de manutenção:", error);
            setMaintenanceTypes([]);
            setTextValidation("Não foi possível carregar os tipos de manutenção.");
            setStatusMessage("error");
            setFieldsIsValid(false);
        } finally {
            setMaintenanceTypesLoading(false);
        }
    }, [cookies.token, setFieldsIsValid, setStatusMessage, setTextValidation]);

    const loadMaintenanceDrivers = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        if (!customerId || customerId <= 0 || !departmentId || departmentId <= 0) {
            setMaintenanceDrivers([]);
            return;
        }

        setMaintenanceDriversLoading(true);

        try {
            const response = await driverFindAllListResponse(
                token,
                customerId,
                departmentId,
            );

            setMaintenanceDrivers(response.data ?? []);
        } catch (error) {
            console.error("Erro ao buscar motoristas:", error);
            setMaintenanceDrivers([]);
            setTextValidation("Não foi possível carregar os motoristas.");
            setStatusMessage("error");
            setFieldsIsValid(false);
        } finally {
            setMaintenanceDriversLoading(false);
        }
    }, [
        cookies.token,
        customerId,
        departmentId,
        setFieldsIsValid,
        setStatusMessage,
        setTextValidation,
    ]);

    const loadMaintenanceChecklists = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        if (!customerId || customerId <= 0 || !vehicleId || vehicleId <= 0) {
            setMaintenanceChecklists([]);
            return;
        }

        setMaintenanceChecklistsLoading(true);

        try {
            const response = await checklistFindAllSimpleResponse(
                token,
                customerId,
                vehicleId,
            );

            setMaintenanceChecklists(response.data ?? []);
        } catch (error) {
            console.error("Erro ao buscar checklists:", error);
            setMaintenanceChecklists([]);
            setTextValidation("Não foi possível carregar os checklists.");
            setStatusMessage("error");
            setFieldsIsValid(false);
        } finally {
            setMaintenanceChecklistsLoading(false);
        }
    }, [
        cookies.token,
        customerId,
        vehicleId,
        setFieldsIsValid,
        setStatusMessage,
        setTextValidation,
    ]);

    const loadMaintenances = useCallback(
        async (
            targetVehicleId: number = vehicleId,
            page: number = 0,
            size: number = 8,
        ) => {
            const token: string = cookies.token || sessionStorage.getItem("token");

            if (
                !targetVehicleId ||
                targetVehicleId <= 0 ||
                !customerId ||
                customerId <= 0
            ) {
                setMaintenanceList([]);
                return;
            }

            setMaintenanceLoading(true);

            try {
                const response = await maintenancesFindAllByActivated(
                    token,
                    customerId,
                    targetVehicleId,
                    "activated",
                    page,
                    size,
                );

                const data = response.data;

                setMaintenanceList(data?.content ?? []);
                setMaintenancePage(data?.pageable?.pageNumber ?? page);
                setMaintenanceSize(data?.pageable?.pageSize ?? size);
                setMaintenanceLastPage(data?.last ?? true);
                setMaintenanceTotalElements(data?.totalElements ?? 0);
            } catch (error) {
                console.error("Erro ao buscar manutenções:", error);
                setMaintenanceList([]);
                setMaintenancePage(0);
                setMaintenanceLastPage(true);
                setMaintenanceTotalElements(0);
                setTextValidation("Não foi possível carregar as manutenções.");
                setStatusMessage("error");
                setFieldsIsValid(false);
            } finally {
                setMaintenanceLoading(false);
            }
        },
        [
            cookies.token,
            customerId,
            vehicleId,
            setFieldsIsValid,
            setStatusMessage,
            setTextValidation,
        ],
    );

    useEffect(() => {
        if (!vehicleId || vehicleId <= 0 || !customerId || customerId <= 0) {
            return;
        }

        setMaintenanceView("list");
        setSelectedMaintenance(null);
        setMaintenancePage(0);

        void Promise.all([
            loadMaintenanceTypes(),
            loadMaintenanceDrivers(),
            loadMaintenanceChecklists(),
            loadMaintenances(vehicleId, 0, maintenanceSize),
        ]);
    }, [
        vehicleId,
        customerId,
        maintenanceSize,
        loadMaintenanceTypes,
        loadMaintenanceDrivers,
        loadMaintenanceChecklists,
        loadMaintenances,
    ]);

    const handleNewMaintenance = useCallback(() => {
        if (!vehicleId || vehicleId <= 0) {
            return;
        }

        setSelectedMaintenance(null);

        setMaintenance({
            ...INITIAL_MAINTENANCE,
            vehicleId,
            customerId,
            maintenanceTypeId: -1,
            files: [],
        });

        setMaintenanceFiles([]);
        setCurrentMaintenanceFile(null);
        setMaintenanceRemovedFiles([]);

        if (maintenanceFileInputRef.current) {
            maintenanceFileInputRef.current.value = "";
        }

        setMaintenanceView("form");
    }, [vehicleId, customerId]);

    const handleEditMaintenance = useCallback(
        (item: MaintenanceResponse) => {
            setSelectedMaintenance(item);

            setMaintenance({
                id: item.id,
                vehicleId: item.vehicleId,
                customerId,
                maintenanceTypeId: item.maintenanceTypeId,
                checklistId: item.checklistId ?? null,
                driverId: item.driverId ?? null,
                maintenanceDate: item.maintenanceDate ?? null,
                odometer: item.odometer ?? null,
                totalValue: item.totalValue != null ? Number(item.totalValue) : null,
                description: item.description ?? null,
                nextMaintenanceDate: item.nextMaintenanceDate ?? null,
                nextMaintenanceKm: item.nextMaintenanceKm ?? null,
                observation: item.observation ?? null,
                // arquivos existentes NÃO vão novamente no PUT
                files: [],
                removedFiles: [],
            });

            // novos anexos
            setMaintenanceFiles([]);
            // IDs dos anexos existentes que forem removidos
            setMaintenanceRemovedFiles([]);
            setCurrentMaintenanceFile(null);

            if (maintenanceFileInputRef.current) {
                maintenanceFileInputRef.current.value = "";
            }

            setMaintenanceView("form");
        },
        [customerId],
    );

    const handleNextMaintenancePage = useCallback(async () => {
        if (maintenanceLastPage || !vehicleId) {
            return;
        }

        const nextPage = maintenancePage + 1;
        await loadMaintenances(vehicleId, nextPage, maintenanceSize);
    }, [
        maintenanceLastPage,
        vehicleId,
        maintenancePage,
        maintenanceSize,
        loadMaintenances,
    ]);

    const handlePreviousMaintenancePage = useCallback(async () => {
        if (maintenancePage <= 0 || !vehicleId) {
            return;
        }

        const previousPage = maintenancePage - 1;
        await loadMaintenances(vehicleId, previousPage, maintenanceSize);
    }, [vehicleId, maintenancePage, maintenanceSize, loadMaintenances]);

    const handleBackMaintenance = useCallback(() => {
        setMaintenance({
            ...INITIAL_MAINTENANCE,
        });

        setSelectedMaintenance(null);
        setMaintenanceFiles([]);
        setMaintenanceRemovedFiles([]);
        setCurrentMaintenanceFile(null);

        if (maintenanceFileInputRef.current) {
            maintenanceFileInputRef.current.value = "";
        }

        setMaintenanceView("list");
    }, []);

    const hasMaintenanceChanges = useCallback(() => {
        const normalizeString = (value?: string | null) => value ?? "";
        const normalizeNumber = (value?: number | null) =>
            value == null ? null : Number(value);
        const normalizeDate = (value?: string | null) =>
            value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
        const normalizeDateTime = (value?: string | null) =>
            value && dayjs(value).isValid()
                ? dayjs(value).format("YYYY-MM-DDTHH:mm:ss")
                : "";

        const hasAttachmentChanges =
            maintenanceFiles.length > 0 || maintenanceRemovedFiles.length > 0;

        if (!selectedMaintenance) {
            return (
                maintenance.maintenanceTypeId > 0 ||
                maintenance.driverId != null ||
                maintenance.checklistId != null ||
                Boolean(normalizeDateTime(maintenance.maintenanceDate)) ||
                maintenance.odometer != null ||
                maintenance.totalValue != null ||
                Boolean(normalizeString(maintenance.description)) ||
                Boolean(normalizeDate(maintenance.nextMaintenanceDate)) ||
                maintenance.nextMaintenanceKm != null ||
                Boolean(normalizeString(maintenance.observation)) ||
                hasAttachmentChanges
            );
        }

        return (
            maintenance.maintenanceTypeId !== selectedMaintenance.maintenanceTypeId ||
            normalizeNumber(maintenance.driverId) !==
            normalizeNumber(selectedMaintenance.driverId) ||
            normalizeNumber(maintenance.checklistId) !==
            normalizeNumber(selectedMaintenance.checklistId) ||
            normalizeDateTime(maintenance.maintenanceDate) !==
            normalizeDateTime(selectedMaintenance.maintenanceDate) ||
            normalizeNumber(maintenance.odometer) !==
            normalizeNumber(selectedMaintenance.odometer) ||
            normalizeNumber(maintenance.totalValue) !==
            normalizeNumber(selectedMaintenance.totalValue) ||
            normalizeString(maintenance.description) !==
            normalizeString(selectedMaintenance.description) ||
            normalizeDate(maintenance.nextMaintenanceDate) !==
            normalizeDate(selectedMaintenance.nextMaintenanceDate) ||
            normalizeNumber(maintenance.nextMaintenanceKm) !==
            normalizeNumber(selectedMaintenance.nextMaintenanceKm) ||
            normalizeString(maintenance.observation) !==
            normalizeString(selectedMaintenance.observation) ||
            hasAttachmentChanges
        );
    }, [
        maintenance,
        maintenanceFiles.length,
        maintenanceRemovedFiles.length,
        selectedMaintenance,
    ]);

    useEffect(() => {
        onUnsavedChangesChange(
            maintenanceView === "form" && hasMaintenanceChanges(),
        );
    }, [hasMaintenanceChanges, maintenanceView, onUnsavedChangesChange]);

    useEffect(
        () => () => onUnsavedChangesChange(false),
        [onUnsavedChangesChange],
    );

    const verifyBeforeCancel = useCallback(() => {
        if (hasMaintenanceChanges()) {
            requestCancelConfirmation(handleBackMaintenance);
            return;
        }

        handleBackMaintenance();
    }, [handleBackMaintenance, hasMaintenanceChanges, requestCancelConfirmation]);

    const handleOpenMaintenanceAttachments = useCallback(
        (item: MaintenanceResponse) => {
            setSelectedMaintenance(item);
            setMaintenanceView("attachments");
        },
        [],
    );

    const handleSaveMaintenance = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        if (!vehicleId || vehicleId <= 0) {
            setTextValidation("Veículo inválido.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (!maintenance.maintenanceTypeId || maintenance.maintenanceTypeId <= 0) {
            setTextValidation("Informe o tipo da manutenção.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (
            !maintenance.maintenanceDate ||
            !dayjs(maintenance.maintenanceDate).isValid()
        ) {
            setTextValidation("Informe a data e hora da manutenção.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        const description = maintenance.description?.trim() ?? "";
        if (!description) {
            setTextValidation("Informe a descrição da manutenção.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (maintenance.odometer != null && maintenance.odometer < 0) {
            setTextValidation("O hodômetro não pode ser negativo.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (maintenance.totalValue != null && maintenance.totalValue < 0) {
            setTextValidation("O valor da manutenção não pode ser negativo.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (
            maintenance.nextMaintenanceKm != null &&
            maintenance.odometer != null &&
            maintenance.nextMaintenanceKm < maintenance.odometer
        ) {
            setTextValidation(
                "O Km da próxima manutenção não pode ser menor que o hodômetro atual.",
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (
            maintenance.nextMaintenanceDate &&
            maintenance.maintenanceDate &&
            dayjs(maintenance.nextMaintenanceDate).isBefore(
                dayjs(maintenance.maintenanceDate),
                "day",
            )
        ) {
            setTextValidation(
                "A data da próxima manutenção não pode ser anterior à manutenção atual.",
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        if (!customerId || customerId <= 0) {
            setTextValidation("Cliente inválido.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        const isUpdate = selectedMaintenance != null && selectedMaintenance.id > 0;

        if (isUpdate && !hasMaintenanceChanges()) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            return;
        }

        const data: MaintenanceRequest = {
            ...maintenance,
            vehicleId,
            customerId,
            description,
            maintenanceDate: dayjs(maintenance.maintenanceDate).format(
                "YYYY-MM-DDTHH:mm:ss",
            ),
            files: maintenanceFiles.map((item) => item.file),
            removedFiles: maintenanceRemovedFiles,
        };

        setMaintenanceSaving(true);

        try {
            if (isUpdate) {
                await maintenanceUpdateByVehicle(token, selectedMaintenance.id, data);
            } else {
                await maintenanceSaveByVehicle(token, data);
            }

            setTextValidation(
                isUpdate
                    ? "Manutenção atualizada com sucesso."
                    : "Manutenção salva com sucesso.",
            );
            setStatusMessage("success");
            setFieldsIsValid(false);

            setMaintenance({
                ...INITIAL_MAINTENANCE,
            });
            setMaintenanceFiles([]);
            setMaintenanceRemovedFiles([]);
            setCurrentMaintenanceFile(null);
            setSelectedMaintenance(null);

            if (maintenanceFileInputRef.current) {
                maintenanceFileInputRef.current.value = "";
            }

            setMaintenanceView("list");
            setMaintenancePage(0);

            await loadMaintenances(vehicleId, 0, maintenanceSize);
        } catch (error: any) {
            console.error(
                isUpdate
                    ? "Erro ao atualizar manutenção:"
                    : "Erro ao salvar manutenção:",
                error,
            );

            const reason = error?.response?.data?.reason;

            if (reason) {
                const errors = reason.split(".");
                setTextValidation(errors[0]);
                setStatusMessage("warn");
            } else {
                setTextValidation(
                    isUpdate
                        ? "Erro ao atualizar a manutenção."
                        : "Erro ao salvar a manutenção.",
                );
                setStatusMessage("error");
            }

            setFieldsIsValid(false);
        } finally {
            setMaintenanceSaving(false);
        }
    }, [
        cookies.token,
        maintenance,
        vehicleId,
        customerId,
        maintenanceSize,
        loadMaintenances,
        maintenanceFiles,
        selectedMaintenance,
        maintenanceRemovedFiles,
        hasMaintenanceChanges,
        setTextValidation,
        setStatusMessage,
        setFieldsIsValid,
    ]);

    const handleMaintenanceFileFullScreen = useCallback(
        (fileUrl: string, fileName: string) => {
            if (document.fullscreenElement) {
                document.exitFullscreen();
                return;
            }

            const element = document.createElement("img");
            element.className = "maintenance-img-anexo";
            element.id = fileName;
            element.src = fileUrl;
            element.style.objectFit = "contain";
            element.style.width = "100%";
            element.style.height = "100%";

            document.body.appendChild(element);

            element.requestFullscreen().catch((error) => {
                console.error("Erro ao abrir anexo em tela cheia:", error);
            });

            element.addEventListener("fullscreenchange", () => {
                if (!document.fullscreenElement && document.body.contains(element)) {
                    document.body.removeChild(element);
                }
            });
        },
        [],
    );

    const handleMaintenanceFileDownload = useCallback(
        (fileUrl: string, fileName: string) => {
            const link = document.createElement("a");
            link.href = fileUrl;

            let extension = "";
            if (fileUrl.startsWith("data:")) {
                const mime = fileUrl.split(";")[0].split(":")[1];
                if (mime && mime.includes("/")) {
                    extension = "." + mime.split("/")[1];
                }
            }

            link.download = fileName.includes(".")
                ? fileName
                : `${fileName || "anexo"}${extension || ".png"}`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        [],
    );

    const handleRemoveMaintenanceFile = useCallback((id: number) => {
        setMaintenanceFiles((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const addMaintenanceFile = useCallback(() => {
        if (!currentMaintenanceFile || currentMaintenanceFile.length === 0) {
            return;
        }

        const selectedFile = currentMaintenanceFile[0];
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);

        reader.onloadend = () => {
            if (typeof reader.result !== "string") {
                return;
            }

            setMaintenanceFiles((prev) => [
                ...prev,
                {
                    id: Date.now() + Math.random(),
                    file: reader.result as string,
                    type: selectedFile.type.substring(0, 5),
                    name: selectedFile.name,
                },
            ]);

            setCurrentMaintenanceFile(null);

            if (maintenanceFileInputRef.current) {
                maintenanceFileInputRef.current.value = "";
            }
        };
    }, [currentMaintenanceFile]);

    useEffect(() => {
        if (currentMaintenanceFile && currentMaintenanceFile.length > 0) {
            addMaintenanceFile();
        }
    }, [currentMaintenanceFile, addMaintenanceFile]);

    const existingMaintenanceFilesCount =
        selectedMaintenance?.files?.filter((fileItem: any) => {
            const attachmentId = typeof fileItem === "object" ? fileItem?.id : null;

            return !attachmentId || !maintenanceRemovedFiles.includes(attachmentId);
        }).length ?? 0;

    const maintenanceFilesCount =
        existingMaintenanceFilesCount + maintenanceFiles.length;

    const getReport = useCallback(
        async (id: number, type: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");

            setMaintenanceExportLoading(true);

            let dataBuffer = new ArrayBuffer(0);

            let reportType = type;
            let isSimpleType = false;

            if (type.includes("simple")) {
                reportType = reportType.split(" ")[0];

                isSimpleType = true;
            }

            try {
                const { data }: any = await maintenanceExportById(
                    token,
                    id,
                    reportType,
                    isSimpleType,
                );

                if ((data as ArrayBuffer).byteLength === 0) {
                    setTextValidation("Nenhum dado para mostrar no relatório.");

                    setStatusMessage("warn");
                    setFieldsIsValid(false);
                } else {
                    setTextValidation("Relatório gerado com sucesso.");

                    setStatusMessage("success");

                    setFieldsIsValid(false);

                    dataBuffer = data;
                }
            } catch (error: any) {
                const data = error?.response?.data;

                let reason = "Erro ao gerar o relatório.";

                if (data instanceof ArrayBuffer) {
                    try {
                        const errorMessage = new TextDecoder().decode(data);

                        reason = JSON.parse(errorMessage)?.reason ?? reason;
                    } catch {
                        // mantém mensagem padrão
                    }
                } else if (data?.reason) {
                    reason = data.reason;
                }

                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(reason);
            } finally {
                setMaintenanceExportLoading(false);
                setReport(dataBuffer);
                setExportType(type);
            }
        },
        [cookies.token, setTextValidation, setStatusMessage, setFieldsIsValid],
    );

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport =
                exportType === "pdf"
                    ? "application/pdf"
                    : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const file = new Blob([report], { type: typeExport });

            const fileURL = URL.createObjectURL(file);

            window.open(fileURL);

            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    return (
        <Paper
            className="tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-p-4 sm:tw-p-5"
            elevation={0}
            sx={{
                background: "#fff",
                padding: { xs: 2, sm: 2.5 },
                border: "1px solid rgba(0, 63, 74, 0.10)",
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
            }}
        >
            {/* LISTAGEM */}

            {maintenanceView === "list" && (
                <>
                    <Box
                        sx={{
                            display: "flex",

                            justifyContent: "space-between",

                            alignItems: "center",

                            marginBottom: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Prometo",

                                fontWeight: "bold",
                            }}
                        >
                            Histórico de manutenções
                        </Typography>

                        <Tooltip title="Criar nova manutenção">
                            <span>
                                <Fab
                                    size="small"
                                    aria-label="Criar nova manutenção"
                                    onClick={handleNewMaintenance}
                                    disabled={isDisabled || maintenanceLoading}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        minHeight: 40,
                                        backgroundColor: "#00B700",
                                        color: "#fff",
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#009900",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    <AddOutlined />
                                </Fab>
                            </span>
                        </Tooltip>
                    </Box>

                    <TableContainer
                        className="tw-overflow-auto tw-rounded-xl tw-border-black/10"
                        component={Paper}
                        sx={{
                            maxHeight: 500,
                            overflow: "auto",
                            scrollbarWidth: "thin",

                            boxShadow: "none",

                            border: "1px solid #e0e0e0",
                            borderRadius: 3,
                        }}
                    >
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {[
                                        "Data",
                                        "Tipo",
                                        "Motorista",
                                        "Odômetro",
                                        "Valor",
                                        "Próxima data",
                                        "Próximo Km",
                                        "Ações",
                                    ].map((title) => (
                                        <TableCell
                                            key={title}
                                            sx={{
                                                fontWeight: "bold",

                                                fontFamily: "Prometo",

                                                background: "#09414A",

                                                color: "#fff",
                                            }}
                                        >
                                            {title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {maintenanceLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ padding: 4 }}>
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                justifyContent="center"
                                                role="status"
                                                aria-live="polite"
                                            >
                                                <CircularProgress size={24} sx={{ color: "#00B700" }} />
                                                <Typography sx={{ fontFamily: "Prometo" }}>
                                                    Carregando manutenções...
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : maintenanceList.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            align="center"
                                            sx={{
                                                padding: 4,

                                                fontFamily: "Prometo",
                                            }}
                                        >
                                            Nenhuma manutenção encontrada.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    maintenanceList.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell>
                                                {item.maintenanceDate
                                                    ? dayjs(item.maintenanceDate).format(
                                                        "DD/MM/YYYY HH:mm:ss",
                                                    )
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>{item.maintenanceTypeName ?? "-"}</TableCell>

                                            <TableCell>{item.driverName ?? "-"}</TableCell>

                                            <TableCell>
                                                {item.odometer != null
                                                    ? item.odometer.toLocaleString("pt-BR")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                {item.totalValue != null
                                                    ? Number(item.totalValue).toLocaleString("pt-BR", {
                                                        style: "currency",

                                                        currency: "BRL",
                                                    })
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                {item.nextMaintenanceDate
                                                    ? dayjs(item.nextMaintenanceDate).format("DD/MM/YYYY")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                {item.nextMaintenanceKm != null
                                                    ? item.nextMaintenanceKm.toLocaleString("pt-BR")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                <Tooltip title="Editar manutenção">
                                                    <IconButton
                                                        disabled={isDisabled}
                                                        onClick={() => handleEditMaintenance(item)}
                                                    >
                                                        <DriveFileRenameOutlineOutlined />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Anexos da manutenção">
                                                    <IconButton
                                                        onClick={() =>
                                                            handleOpenMaintenanceAttachments(item)
                                                        }
                                                    >
                                                        <AttachFileOutlined />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Exportar manutenção">
                                                    <IconButton
                                                        ref={exportAnchorRef}
                                                        aria-label="Exportar manutenção"
                                                        disabled={maintenanceExportLoading}
                                                        onClick={(event) => {
                                                            setMaintenanceExportId(item.id);

                                                            exportAnchorRef.current = event.currentTarget;

                                                            setOpenExport(true);
                                                        }}
                                                    >
                                                        {maintenanceExportLoading &&
                                                            maintenanceExportId === item.id ? (
                                                            <CircularProgress
                                                                size={22}
                                                                sx={{ color: "#00B700" }}
                                                            />
                                                        ) : (
                                                            <PrintOutlined />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>

                                                <Menu
                                                    open={openExport}
                                                    anchorEl={exportAnchorRef.current}
                                                    onClose={() => setOpenExport(false)}
                                                >
                                                    {exportItems.map((option) => (
                                                        <MenuItem
                                                            key={option.label}
                                                            onClick={() => {
                                                                if (maintenanceExportId) {
                                                                    getReport(
                                                                        maintenanceExportId,
                                                                        option.label.toLocaleLowerCase(),
                                                                    );
                                                                }

                                                                setOpenExport(false);
                                                            }}
                                                        >
                                                            {option.icon}

                                                            <Typography
                                                                sx={{
                                                                    marginLeft: 1,
                                                                    fontFamily: "Prometo",
                                                                }}
                                                            >
                                                                {option.label}
                                                            </Typography>
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: "Prometo",
                                color: "gray",
                            }}
                        >
                            Total: {maintenanceTotalElements}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                type="button"
                                variant="outlined"
                                disabled={maintenanceLoading || maintenancePage <= 0}
                                onClick={handlePreviousMaintenancePage}
                                sx={{
                                    fontFamily: "Prometo",
                                    textTransform: "none",
                                }}
                            >
                                Anterior
                            </Button>

                            <Typography
                                sx={{
                                    fontFamily: "Prometo",
                                }}
                            >
                                Página {maintenancePage + 1}
                            </Typography>

                            <Button
                                type="button"
                                variant="outlined"
                                disabled={maintenanceLoading || maintenanceLastPage}
                                onClick={handleNextMaintenancePage}
                                sx={{
                                    fontFamily: "Prometo",
                                    textTransform: "none",
                                }}
                            >
                                Próxima
                            </Button>
                        </Stack>
                    </Box>
                </>
            )}

            {/* FORMULÁRIO */}

            {maintenanceView === "form" && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            marginBottom: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Tooltip title="Voltar" arrow>
                                <IconButton aria-label="Voltar para o histórico de manutenções" onClick={verifyBeforeCancel}>
                                    <ArrowBackOutlined />
                                </IconButton>
                            </Tooltip>

                            <Typography
                                sx={{
                                    fontFamily: "Prometo",
                                    fontWeight: "bold",
                                    fontSize: "17px",
                                }}
                            >
                                {selectedMaintenance ? "Editar manutenção" : "Nova manutenção"}
                            </Typography>
                        </Box>

                        {selectedMaintenance && (
                            <Chip
                                className="crud-metadata-chip"
                                clickable={false}
                                label={`Criado em ${selectedMaintenance.createdAt &&
                                    dayjs(selectedMaintenance.createdAt).isValid()
                                    ? dayjs(selectedMaintenance.createdAt).format(
                                        "DD/MM/YYYY HH:mm",
                                    )
                                    : "-"
                                    } por ${selectedMaintenance.userCreated?.trim() || "-"}`}
                                sx={{
                                    color: "black",
                                    fontFamily: "Prometo",
                                    fontWeight: "bolder",
                                }}
                            />
                        )}
                    </Box>

                    <Box className="row tw-m-0" sx={{ margin: 0 }}>
                        {/* TIPO */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <Autocomplete
                                options={maintenanceTypes}
                                loading={maintenanceTypesLoading}
                                loadingText="Carregando tipos de manutenção..."
                                noOptionsText="Nenhum tipo de manutenção encontrado."
                                value={
                                    maintenanceTypes.find(
                                        (item) => item.id === maintenance.maintenanceTypeId,
                                    ) ?? null
                                }
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, option) =>
                                    setMaintenance((prev) => ({
                                        ...prev,

                                        maintenanceTypeId: option?.id ?? -1,
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Tipo de manutenção"
                                        placeholder="Selecione o tipo de manutenção"
                                        InputLabelProps={{
                                            ...params.InputLabelProps,
                                            shrink: true,
                                        }}
                                    />
                                )}
                                disabled={isDisabled || maintenanceTypesLoading}
                            />
                        </Box>

                        {/* DATA */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale="pt-br"
                            >
                                <MobileDateTimePicker
                                    sx={{
                                        width: "100%",
                                    }}
                                    label="Data da manutenção"
                                    format="DD/MM/YYYY HH:mm:ss"
                                    value={
                                        maintenance.maintenanceDate
                                            ? dayjs(maintenance.maintenanceDate)
                                            : null
                                    }
                                    onChange={(value) =>
                                        setMaintenance((prev) => ({
                                            ...prev,

                                            maintenanceDate:
                                                value && dayjs(value).isValid()
                                                    ? dayjs(value).format(
                                                        "YYYY-MM-DDTHH:mm:ss",
                                                    )
                                                    : null,
                                        }))
                                    }
                                    disabled={isDisabled}
                                    slotProps={{
                                        textField: {
                                            InputLabelProps: { shrink: true },
                                            placeholder:
                                                "Selecione a data e hora da manutenção",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* MOTORISTA */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <Autocomplete
                                options={maintenanceDrivers}
                                loading={maintenanceDriversLoading}
                                loadingText="Carregando motoristas..."
                                noOptionsText="Nenhum motorista encontrado."
                                value={
                                    maintenanceDrivers.find(
                                        (item) => item.id === maintenance.driverId,
                                    ) ??
                                    (maintenance.driverId
                                        ? {
                                            id: maintenance.driverId,
                                            name:
                                                selectedMaintenance?.driverName ??
                                                `Motorista #${maintenance.driverId}`,
                                        }
                                        : null)
                                }
                                getOptionLabel={(option) =>
                                    option.driverCode || option.driverIdentifier
                                        ? `${option.name} - ${option.driverCode ?? ""}${option.driverIdentifier
                                            ? `(${option.driverIdentifier})`
                                            : ""
                                        }`
                                        : option.name
                                }
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, option) =>
                                    setMaintenance((prev) => ({
                                        ...prev,
                                        driverId: option?.id ?? null,
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Motorista"
                                        placeholder="Selecione um motorista (Opcional)"
                                        InputLabelProps={{
                                            ...params.InputLabelProps,
                                            shrink: true,
                                        }}
                                    />
                                )}
                                disabled={isDisabled || maintenanceDriversLoading}
                            />
                        </Box>

                        {/* CHECKLIST */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <Autocomplete
                                options={maintenanceChecklists}
                                loading={maintenanceChecklistsLoading}
                                loadingText="Carregando checklists..."
                                noOptionsText="Nenhum checklist encontrado."
                                value={
                                    maintenanceChecklists.find(
                                        (item) => item.id === maintenance.checklistId,
                                    ) ??
                                    (maintenance.checklistId
                                        ? {
                                            id: maintenance.checklistId,
                                            driverId: null,
                                            checkTypeId: 0,
                                            checkTypeName: "Checklist",
                                            status: "",
                                            createdAt: "",
                                            odometer: 0,
                                        }
                                        : null)
                                }
                                getOptionLabel={(option) => {
                                    const checklistStatus = option.status
                                        ? ` — ${option.status}`
                                        : "";
                                    const checklistDate = option.createdAt
                                        ? ` — ${option.createdAt.replace(/:\d{2}$/, "")}`
                                        : "";

                                    return `#${option.id} — ${option.checkTypeName ?? "Checklist"
                                        }${checklistStatus}${checklistDate}`;
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, option) =>
                                    setMaintenance((prev) => ({
                                        ...prev,
                                        checklistId: option?.id ?? null,
                                    }))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Checklist"
                                        placeholder="Selecione um checklist (Opcional)"
                                        InputLabelProps={{
                                            ...params.InputLabelProps,
                                            shrink: true,
                                        }}
                                    />
                                )}
                                disabled={isDisabled || maintenanceChecklistsLoading}
                            />
                        </Box>

                        {/* ODÔMETRO */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <TextField
                                fullWidth
                                label="Odômetro"
                                type="number"
                                value={maintenance.odometer ?? ""}
                                placeholder="Digite o hodômetro (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) =>
                                    setMaintenance((prev) => ({
                                        ...prev,

                                        odometer:
                                            e.target.value === "" ? null : Number(e.target.value),
                                    }))
                                }
                                inputProps={{
                                    min: 0,
                                    step: 1,
                                }}
                                disabled={isDisabled}
                            />
                        </Box>

                        {/* VALOR */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <TextField
                                fullWidth
                                label="Valor total"
                                type="text"
                                value={
                                    maintenance.totalValue == null
                                        ? ""
                                        : currencyFormatter(
                                            String(
                                                Math.round(
                                                    maintenance.totalValue * 100,
                                                ),
                                            ),
                                        )
                                }
                                placeholder="Digite o valor total (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, "");

                                    setMaintenance((prev) => ({
                                        ...prev,
                                        totalValue:
                                            numericValue === ""
                                                ? null
                                                : Number(numericValue) / 100,
                                    }));
                                }}
                                inputProps={{
                                    inputMode: "numeric",
                                }}
                                disabled={isDisabled}
                            />
                        </Box>

                        {/* DESCRIÇÃO */}

                        <Box
                            className="col-md-12"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <TextField
                                fullWidth
                                required
                                label="Descrição"
                                value={maintenance.description ?? ""}
                                placeholder="Digite a descrição"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) =>
                                    setMaintenance((prev) => ({
                                        ...prev,

                                        description: e.target.value,
                                    }))
                                }
                                inputProps={{
                                    maxLength: 500,
                                }}
                                disabled={isDisabled}
                            />
                        </Box>

                        {/* PRÓXIMA DATA */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale="pt-br"
                            >
                                <DatePicker
                                    sx={{
                                        width: "100%",
                                    }}
                                    label="Próxima manutenção"
                                    format="DD/MM/YYYY"
                                    value={
                                        maintenance.nextMaintenanceDate
                                            ? dayjs(maintenance.nextMaintenanceDate)
                                            : null
                                    }
                                    onChange={(value) =>
                                        setMaintenance((prev) => ({
                                            ...prev,

                                            nextMaintenanceDate:
                                                value && dayjs(value).isValid()
                                                    ? dayjs(value).format("YYYY-MM-DD")
                                                    : null,
                                        }))
                                    }
                                    disabled={isDisabled}
                                    slotProps={{
                                        textField: {
                                            InputLabelProps: { shrink: true },
                                            placeholder:
                                                "Selecione a data da próxima manutenção (Opcional)",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* PRÓXIMO KM */}

                        <Box
                            className="col-md-6"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <TextField
                                fullWidth
                                label="Próxima manutenção (Km)"
                                type="number"
                                value={maintenance.nextMaintenanceKm ?? ""}
                                placeholder="Digite o Km da próxima manutenção (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) =>
                                    setMaintenance((prev) => ({
                                        ...prev,

                                        nextMaintenanceKm:
                                            e.target.value === "" ? null : Number(e.target.value),
                                    }))
                                }
                                inputProps={{
                                    min: 0,
                                    step: 1,
                                }}
                                disabled={isDisabled}
                            />
                        </Box>

                        {/* OBSERVAÇÃO */}

                        <Box
                            className="col-md-12"
                            sx={{
                                margin: "10px 0",
                            }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Observação"
                                value={maintenance.observation ?? ""}
                                placeholder="Digite uma observação (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) =>
                                    setMaintenance((prev) => ({
                                        ...prev,

                                        observation: e.target.value,
                                    }))
                                }
                                inputProps={{
                                    maxLength: 500,
                                }}
                                disabled={isDisabled}
                            />
                        </Box>

                        {/* ANEXOS */}
                        <Box
                            className="col-md-12"
                            style={{
                                margin: "10px 0px 20px 0px",
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: "bolder",
                                    fontFamily: "Prometo",
                                    marginBottom: "10px",
                                }}
                            >
                                Anexos (Opcional)
                            </Typography>

                            {/* INPUT OCULTO */}
                            <input
                                type="file"
                                style={{
                                    display: "none",
                                }}
                                ref={maintenanceFileInputRef}
                                accept="image/*"
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const files = event.target.files;

                                    if (
                                        files &&
                                        files.length > 0 &&
                                        files[0].type.substring(0, 5) === "image"
                                    ) {
                                        setCurrentMaintenanceFile(files);
                                    } else {
                                        setCurrentMaintenanceFile(null);
                                        setTextValidation("Selecione um arquivo de imagem válido.");

                                        setStatusMessage("warn");
                                        setFieldsIsValid(false);
                                    }
                                }}
                            />

                            <Box
                                className="attachment-dropzone"
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    gap: 1,
                                }}
                            >
                                {/* PREVIEWS */}

                                {/* ANEXOS EXISTENTES - EDIÇÃO */}
                                {selectedMaintenance?.files &&
                                    selectedMaintenance.files
                                        .filter((fileItem: any) => {
                                            const attachmentId =
                                                typeof fileItem === "object" ? fileItem?.id : null;

                                            return (
                                                !attachmentId ||
                                                !maintenanceRemovedFiles.includes(attachmentId)
                                            );
                                        })
                                        .map((fileItem: any, index) => {
                                            const fileUrl =
                                                typeof fileItem === "string"
                                                    ? fileItem
                                                    : fileItem?.file || fileItem?.url || "";

                                            const attachmentId =
                                                typeof fileItem === "object" ? fileItem?.id : null;

                                            return (
                                                <Card
                                                    key={attachmentId ?? `existing-${index}`}
                                                    style={{
                                                        background: "#f3eeeec4",
                                                        padding: 5,
                                                        margin: 3,
                                                        borderRadius: 5,
                                                    }}
                                                >
                                                    <img
                                                        src={fileUrl}
                                                        alt={`anexo-${index + 1}`}
                                                        style={{
                                                            objectFit: "cover",
                                                            width: 150,
                                                            height: 150,
                                                            display: "flex",
                                                            boxShadow: "none",
                                                            borderRadius: 5,
                                                            background: "gray",
                                                            margin: 5,
                                                        }}
                                                    />

                                                    <CardActions
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <DownloadOutlined
                                                            titleAccess="Baixar"
                                                            onClick={() =>
                                                                handleMaintenanceFileDownload(
                                                                    fileUrl,
                                                                    `anexo-${index + 1}`,
                                                                )
                                                            }
                                                            style={{
                                                                height: 24,
                                                                width: 24,
                                                                fill: "#00B700",
                                                                cursor: "pointer",
                                                            }}
                                                        />

                                                        {!isDisabled && attachmentId && (
                                                            <HideImageOutlined
                                                                titleAccess="Remover"
                                                                onClick={() =>
                                                                    setMaintenanceRemovedFiles((prev) =>
                                                                        prev.includes(attachmentId)
                                                                            ? prev
                                                                            : [...prev, attachmentId],
                                                                    )
                                                                }
                                                                style={{
                                                                    height: 24,
                                                                    width: 24,
                                                                    fill: "red",
                                                                    cursor: "pointer",
                                                                }}
                                                            />
                                                        )}

                                                        <FullscreenOutlined
                                                            titleAccess="Tela cheia"
                                                            onClick={() =>
                                                                handleMaintenanceFileFullScreen(
                                                                    fileUrl,
                                                                    `anexo-${index + 1}`,
                                                                )
                                                            }
                                                            style={{
                                                                height: 24,
                                                                width: 24,
                                                                fill: "blue",
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    </CardActions>
                                                </Card>
                                            );
                                        })}

                                {/* NOVOS ANEXOS */}
                                {maintenanceFiles.map((item, index) => (
                                    <Card
                                        key={item.id}
                                        style={{
                                            background: "#f3eeeec4",
                                            padding: 5,
                                            margin: 3,
                                            borderRadius: 5,
                                        }}
                                    >
                                        <img
                                            src={item.file}
                                            alt={item.name ?? `anexo-${index + 1}`}
                                            style={{
                                                objectFit: "cover",
                                                width: 150,
                                                height: 150,
                                                display: "flex",
                                                boxShadow: "none",
                                                borderRadius: 5,
                                                background: "gray",
                                                margin: 5,
                                            }}
                                        />

                                        <CardActions
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {/* DOWNLOAD */}
                                            <DownloadOutlined
                                                titleAccess="Baixar"
                                                onClick={() =>
                                                    handleMaintenanceFileDownload(
                                                        item.file,
                                                        item.name ?? `anexo-${index + 1}`,
                                                    )
                                                }
                                                style={{
                                                    height: 24,
                                                    width: 24,
                                                    fill: "#00B700",
                                                    cursor: "pointer",
                                                }}
                                            />

                                            {/* REMOVER */}
                                            {!isDisabled && (
                                                <HideImageOutlined
                                                    titleAccess="Remover"
                                                    onClick={() => handleRemoveMaintenanceFile(item.id)}
                                                    style={{
                                                        height: 24,
                                                        width: 24,
                                                        fill: "red",
                                                        cursor: "pointer",
                                                    }}
                                                />
                                            )}

                                            {/* TELA CHEIA */}
                                            <FullscreenOutlined
                                                titleAccess="Tela cheia"
                                                onClick={() =>
                                                    handleMaintenanceFileFullScreen(
                                                        item.file,
                                                        item.name ?? `anexo-${index + 1}`,
                                                    )
                                                }
                                                style={{
                                                    height: 24,
                                                    width: 24,
                                                    fill: "blue",
                                                    cursor: "pointer",
                                                }}
                                            />
                                        </CardActions>
                                    </Card>
                                ))}

                                {/* ADICIONAR */}
                                {!isDisabled && maintenanceFilesCount < 3 && (
                                    <Box
                                        sx={{
                                            minHeight: maintenanceFilesCount > 0 ? 170 : 45,
                                            display: "flex",
                                            alignItems:
                                                maintenanceFilesCount > 0 ? "center" : "flex-start",
                                        }}
                                    >
                                        <Chip
                                            sx={{
                                                color: "#00B700",

                                                "& .MuiChip-icon": {
                                                    marginLeft: "0px",
                                                },
                                            }}
                                            label="Anexar"
                                            icon={
                                                <Fab
                                                    size="small"
                                                    aria-label="Adicionar anexo"
                                                    onClick={(e) => {
                                                        e.preventDefault();

                                                        maintenanceFileInputRef.current?.click();
                                                    }}
                                                    disabled={maintenanceFilesCount >= 3}
                                                    sx={{
                                                        marginRight: "8px",
                                                        background: "#00B700",
                                                        color: "#fff",
                                                        "&:hover": {
                                                            background: "#00B700",
                                                            color: "#fff",
                                                        },
                                                    }}
                                                >
                                                    <AddOutlined />
                                                </Fab>
                                            }
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    marginTop: 1,
                                    color: "gray",
                                    fontFamily: "Prometo",
                                }}
                            >
                                Máximo de 3 anexos.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        className="tw-flex tw-flex-col-reverse tw-gap-2 tw-border-t tw-border-black/10 tw-pt-4 sm:tw-flex-row sm:tw-justify-end"
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column-reverse", sm: "row" },
                            justifyContent: "flex-end",
                            marginTop: 2,
                            paddingTop: 2,
                            gap: 1,
                            borderTop: "1px solid rgba(0,0,0,0.10)",
                        }}
                    >
                        <Button
                            type="button"
                            onClick={verifyBeforeCancel}
                            disabled={maintenanceSaving}
                            className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            sx={{
                                color: "#fff",
                                marginRight: 1,
                                fontFamily: "Prometo",
                            }}
                        >
                            <CloseOutlined
                                sx={{
                                    marginRight: 1,
                                }}
                            />
                            Cancelar
                        </Button>

                        <Button
                            type="button"
                            onClick={handleSaveMaintenance}
                            disabled={isDisabled || maintenanceSaving}
                            className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            sx={{
                                color: "#fff",
                                fontFamily: "Prometo",
                            }}
                        >
                            {maintenanceSaving ? (
                                <CircularProgress
                                    size={20}
                                    sx={{
                                        color: "#fff",
                                        marginRight: 1,
                                    }}
                                />
                            ) : (
                                <SaveOutlined style={{ color: "#fff", marginRight: 5 }} />
                            )}
                            <Typography
                                className="forgot-password"
                                style={{
                                    fontFamily: "Prometo",
                                    fontSize: "16px",
                                    color: "#ffff",
                                }}
                            >
                                {maintenanceSaving
                                    ? selectedMaintenance
                                        ? "Atualizando..."
                                        : "Salvando..."
                                    : selectedMaintenance
                                        ? "Atualizar"
                                        : "Salvar"}
                            </Typography>
                        </Button>
                    </Box>
                </>
            )}

            {/* ANEXOS DA MANUTENÇÃO */}

            {maintenanceView === "attachments" && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 2,
                        }}
                    >
                        <IconButton
                            title="Voltar para manutenções"
                            onClick={handleBackMaintenance}
                        >
                            <ArrowBackOutlined />
                        </IconButton>

                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "Prometo",
                                    fontWeight: "bold",
                                    fontSize: "17px",
                                }}
                            >
                                Anexos da manutenção
                            </Typography>

                            {selectedMaintenance && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: "Prometo",

                                        color: "gray",
                                    }}
                                >
                                    {selectedMaintenance.maintenanceTypeName}
                                    {" — "}
                                    {selectedMaintenance.maintenanceDate
                                        ? dayjs(selectedMaintenance.maintenanceDate).format(
                                            "DD/MM/YYYY HH:mm:ss",
                                        )
                                        : "Sem data"}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {selectedMaintenance ? (
                        <Box
                            sx={{
                                padding: 3,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                textAlign: "center",
                            }}
                        >
                            {selectedMaintenance.files &&
                                selectedMaintenance.files.length > 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "flex-start",
                                        gap: 1,
                                    }}
                                >
                                    {selectedMaintenance.files.map((fileItem: any, index) => {
                                        const fileUrl =
                                            typeof fileItem === "string"
                                                ? fileItem
                                                : fileItem?.file || fileItem?.url || "";
                                        return (
                                            <Card
                                                key={`existing-${index}`}
                                                style={{
                                                    background: "#f3eeeec4",
                                                    padding: 5,
                                                    margin: 3,
                                                    borderRadius: 5,
                                                }}
                                            >
                                                <img
                                                    src={fileUrl}
                                                    alt={`anexo-${index + 1}`}
                                                    style={{
                                                        objectFit: "cover",
                                                        width: 150,
                                                        height: 150,
                                                        display: "flex",
                                                        boxShadow: "none",
                                                        borderRadius: 5,
                                                        background: "gray",
                                                        margin: 5,
                                                    }}
                                                />

                                                <CardActions
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <DownloadOutlined
                                                        titleAccess="Baixar"
                                                        onClick={() =>
                                                            handleMaintenanceFileDownload(
                                                                fileUrl,
                                                                `anexo-${index + 1}`,
                                                            )
                                                        }
                                                        style={{
                                                            height: 24,
                                                            width: 24,
                                                            fill: "#00B700",
                                                            cursor: "pointer",
                                                        }}
                                                    />

                                                    <FullscreenOutlined
                                                        titleAccess="Tela cheia"
                                                        onClick={() =>
                                                            handleMaintenanceFileFullScreen(
                                                                fileUrl,
                                                                `anexo-${index + 1}`,
                                                            )
                                                        }
                                                        style={{
                                                            height: 24,
                                                            width: 24,
                                                            fill: "blue",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </CardActions>
                                            </Card>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <>
                                    <AttachFileOutlined
                                        sx={{
                                            fontSize: 40,
                                            color: "#6c757d",
                                            marginBottom: 1,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontFamily: "Prometo",
                                        }}
                                    >
                                        Anexos vinculados à manutenção nº {selectedMaintenance.id}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: "Prometo",
                                            color: "gray",
                                            marginTop: 1,
                                        }}
                                    >
                                        Nenhum anexo encontrado para esta manutenção.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: "Prometo",
                                color: "gray",
                            }}
                        >
                            Nenhuma manutenção selecionada.
                        </Typography>
                    )}
                </>
            )}
        </Paper>
    );
};

export default VehicleMaintenance;
